
import Vue, { PropOptions } from 'vue';
import axios, { AxiosResponse } from 'axios';
import qs from 'qs';
import { get, cloneDeep, groupBy } from 'lodash';
import _ from 'lodash';
import cgenericGroup from '../page-groups/CGenericGroup.vue';
import carrayGroup from '../page-groups/CArrayGroup.vue';
import cpopupGroup from '../page-groups/CPopupGroup.vue';
import { EventsService } from '@/services/EventsService';
// import { FeatureFlagsService } from '@/services/FeatureFlagsService';
import { ErrorHandlerService } from '@/services/ErrorHandlerService';
import { mapState, mapGetters } from 'vuex';
import ctabs from '../page-groups/CTabs.vue';
import CPageComments from '../page-items/CPageComments.vue';
import CPrintPage from '../CPrintPage.vue';
import resolveFormulaValue from './resolve-formula-value'
import { PriceChangeService } from '@/services/PriceChangeService';
import { GoodsObjectService } from '@/services/GoodsObjectService';
import { ApiMigrationService } from '@/services/ApiMigrationService';
import { helperFns } from '@/helpers';

type PageFunctionInterface = {
  name: string
  type: string
  args: {
    apiUrl: string
  },
}

type PassedProperties = {
  selectedObject: { [key: string]: any[] }
}

export default Vue.extend({
  name: 'CPage',
  components: { cgenericGroup, carrayGroup, cpopupGroup, ctabs, CPrintPage, CPageComments },
  props: {
    data: {
      type: Object,
      default: () => {}
    },
    initialFunctionsCalled: {
      type: Array,
      default: () => []
    },
    functions: {
      type: Array,
      default: () => []
    } as PropOptions<PageFunctionInterface[]>
  },
  data(): any {
    return {
      loading: 0,
      showPopupGroup: {},
      errorResponse: {},
      variables: {
        params: {},
        query: {},
        formData: {},
        fetchedData : {}
      },
    }
  },
  computed: {
    // ffGroups: function (): any[] {
    //   return FeatureFlagsService.filterGroupsUsingFeatureFlags(this.data.groups);
    // },
    ...mapState(['validation']),
    ...mapGetters(['getGoodFromId']),
    ...mapGetters(['role'])
  },
  watch: {
    $route() {
      this.variables = {
        params: {},
        query: {},
        formData: {},
        fetchedData: {}
      };
      this._invokeOnPageLoad();
    }
  },
  async mounted() {    
    await this._invokeOnPageLoad();
  },
  methods: {
    /**
     * to be invoked when the page is loaded or between different routes using the same Vue
     */
    _invokeOnPageLoad: async function() {
      document.title = this.data.title;
      this.variables.params = this.$route.params;
      this.variables.query = this.$route.query;
      if (this.initialFunctionsCalled.length > 0) {
        await this._callMultipleFunctions(this.initialFunctionsCalled);
      }
      const popupGroupNames: string[] = this.data.groups.filter((item: any) => item.type === 'popupGroup').map((item: any) => item.label);
      let showPopupGroup: { [key: string]: boolean } = {};
      popupGroupNames.forEach((popupGroupName: string) => {
        showPopupGroup = { ...showPopupGroup, [popupGroupName]: false };
      });
      this.showPopupGroup = showPopupGroup;
    },
    /**
     * returns a JSON or function or variable value based on the input `dataVariable`.
     * @param dataVariable the variable which can represent a JSON or function or a variable
     */
    _retrieveApiBody: function(dataVariable: string): any {
      try {
        return JSON.parse(dataVariable);         
      } catch (error) {
        const _function = this._callFunction(dataVariable);
        if(_function) {
          return _function;
        } else {
          return this._getVariable(dataVariable);            
        }
      }
    },
    /**
     * returns a Promise which will submit the form data to a HTTP POST request 
     * @param apiUrl the URL for the POST request
     * @param redirectUrl (optional) The URL to which the app is redirected on successful submission.
     * @param fnsAfterSubmit (optional) The functions to be invoked after successful completion of POST API. Can be used when redirectUrl needs to be dynamic and/or uses params/queries.
     * @param postData (optional) Data which needs to be passed as body, which can be static JSON, function name which will return or the variable name
     * If not used, the default body value for API will be ‘formData’.
     * @param refreshPage (optional) boolean flag used to refresh the current page if needed.
     * @param requestMethod (optional) type of API method, defaults to POST.
     */
    fnSubmit: async function(
      { apiUrl, redirectUrl, fnsAfterSubmit, postData, storeResponse, refreshPage, validationVariable, requestMethod, serviceConfig } : { apiUrl: string, redirectUrl?: string, fnsAfterSubmit?: string, postData?: string, storeResponse?: string, refreshPage?: string, validationVariable?: string, requestMethod?: string, serviceConfig?: string },
      sourceObject?: any
    ): Promise<any> {
      try {
        this.$store.commit('setFormValidStatus', { default: {} });
        if (validationVariable) {
          await this.$nextTick();
          EventsService.triggerEvent(`${validationVariable}Validation`);
        }
        if(!validationVariable  || this.validation[`${validationVariable}`]) {
          this.loading = this.loading + 1;
          if(serviceConfig) {
            await this._invokeService(serviceConfig, sourceObject);
          }
          const requestData: any = _.cloneDeep(postData ? this._retrieveApiBody(postData) : this.variables.formData);
          // NESTJS MIGRATION: 1. Resolve original URL 
          const originalResolvedUrl = this._resolveTemplateString(apiUrl, sourceObject);
          // NESTJS MIGRATION: 2. Pass through API Migration Router
          const { resolvedUrl, transformResponse, overrideMethod } = ApiMigrationService.getRoutedApi(originalResolvedUrl, requestData);
          const resolvedRequestMethod = overrideMethod ? overrideMethod : (requestMethod || 'POST');
          // NESTJS MIGRATION: @ts-ignore
          const response: AxiosResponse<any> = await axios({
            method: resolvedRequestMethod,
            url: resolvedUrl, // NESTJS MIGRATION: Use the potentially migrated URL
            data: requestData,
            ...(resolvedRequestMethod == 'GET' && { params: requestData })
          }); 
          // NESTJS MIGRATION: 3. Extract and Transform Response
          let responseData = response.data;
          if (transformResponse) {
            responseData = transformResponse(responseData);
          }
          // NESTJS MIGRATION: 4. Use 'responseData' instead of 'response.data' for the rest of the function
          this.$store.commit('setSnackbar', { serverMessage: responseData.message, show: true, color: 'primary' });
          if(storeResponse) {
            this.variables.response = responseData;
          }
          if(redirectUrl) {
            this.$router.push(redirectUrl);
          }
          if(fnsAfterSubmit) {
            await this._callMultipleFunctions(fnsAfterSubmit);
          }
          if(refreshPage) {
            this.variables.formData = {};
            this.variables.fetchedData = {};
            this._invokeOnPageLoad();
          }
        }
      } catch (error) {
        ErrorHandlerService.handleError(error);
      }
      this.loading = this.loading - 1;
    },
    /**
     * returns a Promise which will get initial data through HTTP GET request
     * @param apiUrl the URL for the GET request
     * @param storeInVar the variable where the data from GET request will be stored
     * @param postData (optional) Data which needs to be passed as body, which can be static JSON, function name which will return or the variable name
     * If not used, the default body value for API will be ‘formData’.
     * @param sourceTransformationFn (optional) name of function to be invoked to transform the object before storing it
     * @param fnsAfterSubmit (optional) The functions to be invoked after successful completion of POST API.
     * @param triggerEvent (optional) Custom CEvent to be triggered after API is completed
     * @param addToState (optional) used if the response data needs to be added to Vuex State
     */
    fnGetData: async function(
      { apiUrl, storeInVar, postData, sourceTransformationFn, fnsAfterSubmit, triggerEvent, addToState, requestMethod, serviceConfig }: { apiUrl: string, storeInVar: string, postData?: string, sourceTransformationFn?: string,  fnsAfterSubmit?: string, triggerEvent?: any, addToState?: { actionName: string, variable: string }, requestMethod?: string, serviceConfig?: string },
      sourceObject?: any
    ): Promise<any> {
      try {
        const requestData: any = postData ? this._retrieveApiBody(postData) : '';
        this.loading = this.loading + 1;
        const originalResolvedUrl = this._resolveFormulaValue(apiUrl,{ _item: sourceObject, _object: sourceObject });
        const { resolvedUrl, transformResponse } = ApiMigrationService.getRoutedApi(originalResolvedUrl, requestData);
        const resolvedRequestMethod = requestMethod ? requestMethod : 'GET';
        const isNewApi = resolvedUrl.includes('/api/v3/');
        const response: AxiosResponse<any> = await axios({
          method: resolvedRequestMethod,
          url: resolvedUrl,
          data: requestData,
          ...(resolvedRequestMethod == 'GET' && { 
            params: requestData,
            ...(isNewApi && {
              paramsSerializer: (params) => {
                const processedParams = { ...params };
                Object.keys(processedParams).forEach(key => {
                  if (Array.isArray(processedParams[key])) {
                    processedParams[key] = JSON.stringify(processedParams[key]);
                  }
                });
                return qs.stringify(processedParams);
              }
            })
          })
        });
        let responseData = response.data;
        if (transformResponse) {
          responseData = transformResponse(responseData);
        }
        if(sourceTransformationFn) {
          responseData = this._callFunction(sourceTransformationFn, responseData);
        }
        if(storeInVar === 'formData') {
          responseData = { ...this._getVariable(storeInVar), ...responseData };
        }
        const { prefix, key } = this._getPrefixAndKey(storeInVar);
        if(!this._getVariable(prefix)) {
          const { prefix: parentPrefix, key: parentKey } = this._getPrefixAndKey(prefix);
          this.$set(this._getVariable(parentPrefix), parentKey, {});
          this.$set(this._getVariable(prefix), key, responseData);
        } else {
          this.$set(this._getVariable(prefix), key, responseData);
        }
        if(triggerEvent) {
          EventsService.triggerEvent(triggerEvent.event, this._getVariable(triggerEvent.eventDataVariable));
        }
        if(addToState) {
          this.$store.commit(addToState.actionName, this._getVariable(addToState.variable));  
        }
        if(serviceConfig) {
          await this._invokeService(serviceConfig, sourceObject);
        }
        if(fnsAfterSubmit) {
          await this._callMultipleFunctions(fnsAfterSubmit);
        }
      } catch (error) {
        this.$store.commit('setSnackbar', { serverMessage: error.response.data.message || error.message, show: true, color: 'error' })
      }
      this.loading = this.loading - 1;
    },
    /**
     * Used to copy data from source to destination
     * @param sourceKey key to copy from (will be prefixed with 'variables'). Note: if `__selectedObject` was passed, it will take it from passedProperties
     * @param sourceFn (optional) function used to transform the source data retrieved using `sourceKey`. 
     * @param destinationKey key to copy to (will be prefixed with 'variables.fetchedData')
     * @param destinationKeyIndex (optional) Index value  to copy to if 'destinationKey' is an Array. eg: fetchedData.products[0]
     * @param destinationKeyType (optional) the type of the data to be stored, currently 'Number', 'Array', 'Object'
     * @param append (optional) used to denote whether to replace or add to existing data. If `TRUE`, used with `destinationKeyType`
     * @param passedProperties generic properties passed down from any function
     */
    fnCopyData: function({sourceKey, sourceFn, destinationKey, destinationKeyIndex, destinationKeyType, append }: { sourceKey: string, sourceFn?: string, destinationKey: string, destinationKeyIndex?: string, destinationKeyType?: string, append?: string}, passedProperties?: PassedProperties) {
      const appendFlag = (append && append == 'TRUE') || false;
      let sourceData = 
        sourceKey ? 
          cloneDeep(
            this._getValueFromFormat(sourceKey, passedProperties ? (passedProperties.selectedObject ? passedProperties.selectedObject : passedProperties) : ''))
          : '';
      if(sourceFn) {
        sourceData = this._callFunction(sourceFn, sourceData);
      }
      const updatedDestinationKey = destinationKeyIndex ? `${destinationKey}.${this._resolveFormulaValue(destinationKeyIndex)}`: destinationKey;
      const { prefix, key } = this._getPrefixAndKey(updatedDestinationKey);
      const existingData = this._getVariable(updatedDestinationKey);
      if (destinationKeyType == 'Number') {
        sourceData = Number(sourceData);
      } else if(appendFlag && destinationKeyType == 'Object') {
        sourceData = { ...existingData, ...sourceData };
      } else if(appendFlag && destinationKeyType == 'Array') {
        sourceData = [ ...existingData, ...sourceData ];
      }
      this.$set(this._getVariable(prefix), key, sourceData );
    },
    /**
     * Used to add new entry from sourceVariable/sourceFn to an array (eg: adding objects to a table)
     * @param sourceVariable variable name where the object is stored
     * @param sourceFn name of function which will be used to find the object
     * @param destinationVariable variable name which stores the array of table columns
     * @param sourceTransformationFn name of function to be invoked to transform the object before pushing to array. 
     */
    fnPushToArray: function(
      { sourceVariable, sourceFn, destinationVariable, sourceTransformationFn }:
        { sourceVariable?: string, sourceFn?: string, destinationVariable: string, sourceTransformationFn?: string},
      passedProperties?: PassedProperties
    ) {
      let newItem: any = {};
      if(sourceVariable) {
        newItem = this._getValueFromFormat(sourceVariable, _.get(passedProperties, 'selectedObject', passedProperties));
      } else if (sourceFn) {
        newItem = this._callFunction(sourceFn, passedProperties);
      } else {
        this.$store.commit('setSnackbar', { serverMessage: 'fnPushToArray: new item not found', show: true, color: 'error' })
      }
      if(sourceTransformationFn) {
        newItem = this._callFunction(sourceTransformationFn, newItem);
      }
      const destinationArray: any[] = this._getVariable(destinationVariable) || [];
      destinationArray.push(newItem);
      const { prefix, key } = this._getPrefixAndKey(destinationVariable);
      this.$set(this._getVariable(prefix), key, destinationArray);
    },
    /**
     * Used to create a new array by tranforming or updating an input array
     * @param sourceVariable variable name where the object is stored
     * @param sourceFn name of function which will be used to find the object
     * @param destinationVariable variable name which stores the array of table columns
     * @param arrayTransformationFn name of function to be invoked to transform each iteration in input array. 
     */
    fnCreateArray: function(
      { sourceVariable, sourceFn, destinationVariable, arrayTransformationFn, mergeArray, mergedObjectKey }:
        { sourceVariable?: string, sourceFn?: string, destinationVariable: string, arrayTransformationFn: string, mergeArray?: string, mergedObjectKey?: string },
      passedProperties?: PassedProperties
    ) {
      let sourceArray: any[] = [];
      if(sourceVariable) {
        sourceArray = this._getValueFromFormat(sourceVariable, _.get(passedProperties, 'selectedObject', passedProperties));
      } else if (sourceFn) {
        sourceArray = this._callFunction(sourceFn);
      } else {
        this.$store.commit('setSnackbar', { serverMessage: 'fnCreateArray: source array not found', show: true, color: 'error' });
      }
      let destinationArray: any[] = [];
      sourceArray.forEach((sourceItem: any) => {
        const transformedObject = this._callFunction(arrayTransformationFn, sourceItem);
        if(mergeArray === 'TRUE') {
          const arrayToBeMerged: any[] = mergedObjectKey ? _.get(transformedObject, mergedObjectKey) : transformedObject;
          destinationArray = [ ...destinationArray, ...arrayToBeMerged ];
        } else {
          destinationArray.push(transformedObject);
        }
      });
      const { prefix, key } = this._getPrefixAndKey(destinationVariable);
      this.$set(this._getVariable(prefix), key, destinationArray);
    },
    /**
     * Used to find an object from an array of objects based the `searchKey` and the corresponding `searchValue`.
     * @param sourceVariable the variable name of the input array of objects.
     * @param searchKey name of the key of the object to be found.
     * @param searchValue variable name of the value to be matched. The result object[searchKey] will have this corresponding value. 
     */
    fnFindInArray: function(
      { sourceVariable, searchKey, searchValue }:
        { sourceVariable: string, searchKey: string, searchValue: string },
      sourceObject: any
    ): any {
      const sourceArray: any[] = this._getVariable(sourceVariable);
      return sourceArray.find(sourceItem => _.get(sourceItem, searchKey) == this._getValueFromFormat(searchValue, sourceObject));
    },
    /**
     * Used to find an object from an array of objects based the `searchKey` and the corresponding `searchValue`.
     * @param sourceVariable the variable name of the input array of objects.
     * @param searchKey name of the key of the object to be found.
     * @param searchValue variable name of the value to be matched. The result object[searchKey] will have this corresponding value. 
     */
    fnFilterArray: function(
      { sourceVariable, searchKey, searchValue, searchKeyValueType, filterCondition, formulaCondition }:
        { sourceVariable: string, searchKey: string, searchValue: string, searchKeyValueType?: string, filterCondition?: string, formulaCondition?: string }
    ): any {
      const sourceArray: any[] = this._getVariable(sourceVariable);
      const resolvedSearchValue = typeof(searchValue) === 'string' ? this._resolveFormulaValue(searchValue) : searchValue;
      return sourceArray.filter(sourceObject => {
        if(formulaCondition) {
          return this._resolveFormulaValue(formulaCondition, { _item: sourceObject });
        }
        if(searchKeyValueType && searchKeyValueType === 'Array'){
          return _.get(sourceObject, searchKey).includes(resolvedSearchValue);
        } else {
          if(filterCondition) {
            return eval(`"${_.get(sourceObject, searchKey)}" ${filterCondition} "${resolvedSearchValue}"`);
          } else {
            return _.get(sourceObject, searchKey) === resolvedSearchValue;
          }
        }
      });
    },
    /**
     * Used to create a new object from `sourceObject` with the help of `newKeyValues`
     * @param sourceObject the input object which is used to retrieve relevant values defined by `newKeyValues`
     * @param newKeyValues key-value pair object where the `key` represents the `key` of the new object and 
     * `value` specifies the source of the value in `sourceObject`
     * @param useFormula (optional) flag if there's a need to use formula to create objects
     * @param staticObject (optional) flag if the object in newKeyValues can be returned without any modifications
     * @param additionalVariable (optional) used when a different variable is needed to process the needed values
     */
    fnCreateObjectFromKeys({ newKeyValues, useFormula, staticObject, additionalVariable }: { newKeyValues: any, useFormula?: string, staticObject?: true, additionalVariable?: string }, sourceObject: any ) : any {
      let formattedObj = {};
      if(staticObject) {
        return _.cloneDeep(newKeyValues);
      }
      Object.keys(newKeyValues).forEach(key => {
        const value = useFormula ? this._resolveFormulaValue(
          newKeyValues[key],
          { _object: sourceObject, _variable: additionalVariable ? this._getVariable(additionalVariable) : {} }
        ) : this._getValueFromFormat(newKeyValues[key], sourceObject);
        formattedObj = { ...formattedObj, [key]: value };
      });
      return _.cloneDeep(formattedObj);
    },
    /**
     * Used to redirect the application
     * @param redirectUrl the URL where the application will be redirected
     * @param queryParams the dynamic queries to be send along with path.
     * @param openNewTab (optional) use to open URL in new tab
     * @param replace (optional) use to replace url history in new tab (after redirect)
     */
    fnRedirect({ redirectUrl, queryParams, openNewTab, replace }: { redirectUrl: string, queryParams?: any, openNewTab?: string, replace?: string }, sourceObject?: any) : any {
      const resolvedUrl: string = this._resolveFormulaValue(redirectUrl, { _item: sourceObject, _object: sourceObject });
      if(!resolvedUrl) {
        this.$store.commit('setSnackbar', { serverMessage: 'fnRedirect: invalid URL', show: true, color: 'error' });
        return;
      }
      let query: any = {};
      if(queryParams) {
        Object.keys(queryParams).forEach(key => {
          query[key] = this._resolveFormulaValue(queryParams[key],{ _item: sourceObject, _object: sourceObject });
        });
      }
      const matchQueries = (newQueries: any) => {
        return Object.keys(newQueries).map(key => newQueries[key] == this.$route.query[key]).reduce((flag, currentValue) => flag && currentValue, true);
      }
      if(openNewTab == 'TRUE') {
        if(resolvedUrl.startsWith('https://')) {
          window.open(resolvedUrl, '_blank');  
        } else {
          const routeData = this.$router.resolve({path: resolvedUrl, query });
          window.open(routeData.href, '_blank');
        }
      } else if(resolvedUrl !== this.$route.path || !matchQueries(query)) {
        if(replace == 'TRUE') {
          this.$router.replace({ path: resolvedUrl, query });
        } else {
          this.$router.push({ path: resolvedUrl, query });
        }
      }
    },
    /**
     * Print the page 
     */
    fnPrint() {
      window.print();
    },
    /**
     * Used to send snackbar messages
     * @param serverMessage the message to be shown.
     * @param color ('error'=> red, 'success'=> green).
     */
    fnOpenSnackbar({serverMessage, color }: { serverMessage: string, color: string }) {
      this.$store.commit('setSnackbar', { serverMessage, show: true, color });
    },
    /**
     * Invoked when there is a change in the value associated with a component 
     * @param functionsCalled list of functions in the format `function1,function2` which need to invoked using `_callFunction` method.
     * @param selectedObject the object associated with the value which will be passed to the above mentioned functions.
     */
    _onChange: async function(functionsCalled: any, { selectedObject } : PassedProperties) {
      if (functionsCalled) {
        await this._callMultipleFunctions(functionsCalled, { selectedObject });
      }
    },
    /**
     * resolves template string with dynamic values by replacing `{{variable}}` with the `variable` value.
     * @param template the input template string
     */
    _resolveTemplateString: function(template: string, sourceObject?: any): string {
      const resolvedTemplateString = template ? 
        template.replace(/\{{2}([a-zA-Z0-9._]+)\}{2}/g, (match: string, p1: string) => {
          const variableValue = this._getValueFromFormat(p1, sourceObject);
          return variableValue ? variableValue : '';
        }) : '';
      return resolvedTemplateString;
    },
    /**
     * resolves function args from function config data.
     * @param template the input template string
     */
    _mapFunctionArgs(pageFunction : any): any { // @TODO: remove this function when Functions are properly defined
      let i = 1;
      let args = {};
      while(pageFunction[`arg${i}Key`] != null && pageFunction[`arg${i}Key`] != '') {
        let argsValue = pageFunction[`arg${i}Value`];
        try {
          argsValue = JSON.parse(argsValue);
        } catch (error) {
          if (/^\{[^{]/g.test(argsValue)) {
            // eslint-disable-next-line
          }
        }
        if(pageFunction[`arg${i}Key`] === 'fnsAfterSubmit' && typeof argsValue === 'string') {
          argsValue = [ { name: argsValue, executionOrder: 1 }]; // @TODO: can be removed when functions config is defined properly
        } 
        // @ts-ignore
        args[pageFunction[`arg${i}Key`]] = argsValue;
        i++;
      }
      if(i == 1) { // means the function arguments is defined directly in the object
        return { ...args, ...pageFunction };
      }
      return args;
    },
    /**
     * Returns the result of a function with the name `functionName` under the `functions` prop
     * @param functionName denotes the name of function to be found in `functions` prop
     * @param event (optional) additional argument to be passed alongwith the arguments defined in `functions` prop.
     */
    _callFunction(functionName: string, event?: any): any {
      const targetFunction: PageFunctionInterface | undefined = this.functions.find((fn: any) => fn.name == functionName);
      if (targetFunction) {
        // @ts-ignore
        return this[targetFunction.type](this._mapFunctionArgs(targetFunction), event);
      }
    },
    /**
     * used to execute multiple functions in parallel and/or serial based on `executionOrder`
     * @param functionsConfig JSON-like string which is used to define the functions and their `executionOrder`.
     * If not a JSON-like string, assumed to be a single function name 
     * @param event (optional) additional argument to be passed alongwith the arguments defined in `functionsConfig` prop.
     */
    async _callMultipleFunctions(functionsConfig: any, event?: any) {
      const functionGroups = groupBy(functionsConfig, 'executionOrder');
      const executionStages: any[] = Object.keys(functionGroups).map((functionGroupKey: string) => {
        return () => {
          const functions = functionGroups[functionGroupKey].map(functionObject => {
            if(functionObject.executeIf && !this._resolveFormulaValue(functionObject.executeIf, { _item: event })) {
              return;
            }
            if(functionObject.event) {
              EventsService.triggerEvent(functionObject.event, this._getVariable(functionObject.eventDataVariable));
            }
            if (functionObject.pageFunction) {
              // @ts-ignore
              return this[functionObject.pageFunction.type](this._mapFunctionArgs(functionObject.pageFunction), event);
            } else {
              return this._callFunction(functionObject.name, event);
            }
          });
          return Promise.all(functions);
        }
      });
      for (const currentExecutionStage of executionStages) {
        await currentExecutionStage();
      }
    },
    /**
     * Invoke service Functions before `fnSubmit`
     * @param service the service function name
     * @param args the arguments required for the service function
     * @param destinationKey destination variable name
     * 
     */
    async _invokeService({ service, args, destinationKey }:{ service: string, args: string[], destinationKey?: string }, sourceObject?: any) {
      const resolvedArguments = args.map((arg: string) => this._getValueFromFormat(arg, sourceObject));
      let destinationValue: any = {};
      if(service == 'PRICE_CHANGE_REQUEST') {
        const [ ordersArray ] = resolvedArguments;
        destinationValue = await PriceChangeService.createPriceChangedOrders(ordersArray);
      } else if(service == 'APPROVE_PRICE_CHANGE') {
        const ordersArray = resolvedArguments[0];
        destinationValue = await PriceChangeService.approvePriceChange(ordersArray);
      } else if(service == 'GET_WIP') {
        const prodReport = resolvedArguments[0];
        await GoodsObjectService.getWipsFromProductionReport(prodReport);
      }
      if(destinationKey) {
        const { prefix, key } = this._getPrefixAndKey(destinationKey);
        if(!this._getVariable(prefix)) {
          const { prefix: parentPrefix, key: parentKey } = this._getPrefixAndKey(prefix);
          this.$set(this._getVariable(parentPrefix), parentKey, {});
          this.$set(this._getVariable(prefix), key, destinationValue);
        } else {
          this.$set(this._getVariable(prefix), key, destinationValue);
        }
      }
    },
    /**
     * Returns object value from a string (e.g. _getVariable('car.key.first') returns the `this.variables.car.key.first` object)
     * @param varName the key to use after the object is fetched from this + `startingKey`
     * @param startingKey the key to use after `this` (e.g. if startingKey is `car.first` then we will start from `this.car.first`)
     * @param findWithinStartingKey (optional) if specified, will peforma a search inside `startingKey` and will compare this.`startingKey`.`varName` to `findWithinStartingKey`
     * 
     */
    _getVariable(varName : string, { startingKey, findWithinStartingKey } : { startingKey?: string, findWithinStartingKey?: string } = {}) : any {
      let targetVariable : any = this.variables;
      if (startingKey) {
        targetVariable = get(targetVariable, startingKey);
      }
      if (findWithinStartingKey) {
        return targetVariable ? targetVariable.find((item: any) => item[varName] === findWithinStartingKey) : {}; 
      } else {
        if (varName != '') {
          targetVariable = get(targetVariable, varName);
        }
        return targetVariable;
      }
    },
    /**
     * Used to transform 'car.key.first' -> prefix: 'car.key' & key: 'first'
     * @param varName the string that is needed to convert (e.g. 'car.key.first')
     */
    _getPrefixAndKey(varName: string) : { prefix: string, key: string} {
      const splitDots = varName.split('.');
      const key = splitDots.pop() || '';
      const prefix = splitDots.join('.');
      return { prefix, key };
    },
    /**
     * Used to retrieve values from variables with formats like `__params` and `__selectedObject`
     * @param formatString the string with the required format
     * @param sourceVariable (optional) the source from which the value is retrieved
     */
    _getValueFromFormat(formatString: string, sourceVariable?: any): any {
      if(formatString.startsWith('__params.')) {
        return this.variables.params[formatString.split('__params.')[1]];
      } else if(formatString.startsWith('__query.')) {
        return this.variables.query[formatString.split('__query.')[1]];
      } else if(formatString.startsWith('__selectedObject')) {
        return formatString.split('__selectedObject.')[1] ? get(sourceVariable, formatString.split('__selectedObject.')[1]) : sourceVariable;
      } else if(formatString.startsWith('__item')) {
        return formatString.split('__item.')[1] ? get(sourceVariable, formatString.split('__item.')[1]) : sourceVariable;
      } else if(formatString.startsWith('__response.')) {
        return get(this.variables.response, formatString.split('__response.')[1]);
      } else {
        return this._getVariable(formatString);
      }
    },
    /**
     * Used to retrieve the visibility status of a component or group using the `showIf` flag in `pageItems`
     * @param showVariable string which can be a variable name or an equality statement 
     */
    _getVisibilityStatus(showVariable: string): boolean {
      if(!showVariable) {
        return true;
      }
      return this._resolveFormulaValue(showVariable)
    },
    _getDisableStatus(disableStatusString: string): boolean {
      if(!disableStatusString) {
        return false;
      }
      if(disableStatusString === 'TRUE') {
        return true;
      }
      if(disableStatusString === 'FALSE') {
        return false;
      }
      return this._resolveFormulaValue(disableStatusString);
    },
    /**
     * Used to calculate the value based on a formula specified by `formulaString` using `item` and `formVariable`
     * @param formulaString the formula which needs to be evaluated
     * @param variables an object which is used to retrieve values in `__item.value` and `_variable.value` format
     * @param index (optional) used to replace index in arrays
     */
    _resolveFormulaValue(formulaString: string, variables?: { _item?: any, _variable?: any, _object?: any }, index?: number): any {
      return resolveFormulaValue(this, formulaString, variables, index);
    },
    /**
     * Used to return values based on availability of filters in the config
     * @param configVariable the variable which specifies if the filter is used or not
     * @param filterInputValue the input value used to apply filter  
     * @param (optional) filterParam additional param to be passed to filter  
     */
    _checkFilter(configVariable: any, filterInputValue: any, filterParam?: any): any {
      return configVariable && configVariable.filterType ?  helperFns[configVariable.filterType](filterInputValue, filterParam) : filterInputValue;
    },
    fnOpenPopup({ popupName }: { popupName: string }) {
      this.$set(this.showPopupGroup, popupName, true);
    },
    fnClosePopup({ popupName }: { popupName: string }) {
      this.$set(this.showPopupGroup, popupName, false);
    },
    _getPrintDisplayClass(config?: any): string {
      if(config) {
        if(config.printable === true) {
          return 'd-none d-print-block';
        } else if (config.printable === false) {
          return 'd-print-none';
        }
        return '';
      }
      return '';
    }
  }
});
